import type { IPartnersCompany } from 'app/services/partnersApi';
import { NON_CLICKABLE_OPAQUE } from '../readOnly';

/**
 * Phase 7a — `.col-xs-5.address-content`, a 1:1 port of `tmpl/partners.tmpl:104-201`.
 *
 * ★ THE SIBLING TRIPLE IS LOAD-BEARING (discovery §6.1). Every field is
 * `<input> → <i class="text-clear …> → <span class="placeholder-label*">` as
 * IMMEDIATE siblings, because the floating-label CSS is written as
 *   `input:focus + i + .placeholder-label`                (styles.css:241)
 *   `input:not(:placeholder-shown) + i + .placeholder-label`   (:489)
 *   `input:not(:placeholder-shown) + i + .placeholder-label-{city,address-state,
 *      postal-code,address-country}`                          (:499-502)
 * Dropping the `<i>`, wrapping the span, or emitting the label first kills the
 * float on every input. The non-empty `placeholder` attribute is equally
 * load-bearing: `:placeholder-shown` is what makes a PRE-FILLED input show its
 * label raised without focus.
 *
 * ★ FIVE label classes are in play, not one: `.placeholder-label` (Address
 * Line 1/2, Telephone, Email, Notes), `-city`, `-address-state`,
 * `-postal-code`, `-address-country`. They are NOT interchangeable — only
 * `-city` and `-address-state` have an `:not(:placeholder-shown)` rule of their
 * own at :274/:305 in addition to the shared :499-502 block.
 *
 * ★ WHITESPACE IS REPRODUCED DELIBERATELY. The template has NO whitespace
 * between `<input>` and `<i>` here (they are written flush) but a newline —
 * i.e. a collapsing space text node — between `<i>` and `<span>`. `.text-clear`
 * is `position:relative; display:inline-block; margin-left:-15px`
 * (common.css:28130-28144), so it sits in normal flow and stray text nodes are
 * visible layout. The contact rows are written differently (see ContactRow) and
 * are reproduced differently on purpose.
 *
 * ★ QUIRKS MIRRORED (do not "fix"):
 *  - `address_line_one`, `address_line_two`, `email` and `notes` carry NO
 *    `maxlength` while their columns are varchar(50)/varchar(200) — over-length
 *    input 500s server-side and the UI shows nothing (discovery §4.7, on the
 *    owner's list).
 *  - `tel_office` carries a pointless `minlength="0"`.
 *  - the phone row's icon `<span>` carries the `address_field` CLASS on a
 *    non-input element (`change` can never fire on a span) — harmless, kept.
 *  - `tel_office` / `email` use `i.address-text-clear.text-clear.icon-close-1`;
 *    `address-text-clear` matches no rule anywhere. Kept.
 *  - the empty `<div class="">` wrappers are real nodes in the legacy DOM
 *    (verified in the live outline) — they are kept so the Bootstrap grid
 *    nesting, and therefore the gutters, are identical.
 *
 * ★ 7c — `change .address_field` (§4.3) and `click .text-clear` (§4.4) are
 * DELEGATED on `#ksdm-content` by `PartnersPage` (`partners.js:32`, `:27`), so
 * this component still binds nothing. Two consequences worth knowing here:
 *  - the inputs stay UNCONTROLLED (`defaultValue`), exactly like the legacy DOM.
 *    Every keystroke is the user's alone; a PATCH goes out once, on the native
 *    `change`. React's `onChange` would fire per keystroke and must never be
 *    used on these.
 *  - a `defaultValue` that changes after mount does NOT reset a dirty input
 *    (React assigns the `value` ATTRIBUTE), which is what keeps a field-edit
 *    cache update from yanking the caret. The one thing that DOES rebuild these
 *    inputs from the model is a `render()`, and `PartnersPage` reproduces that
 *    with a remount.
 *  - `type="email"` on the email input is what gives `checkValidity()` something
 *    to fail, i.e. it is the whole of §4.3's revert-without-request branch.
 */
interface AddressFormProps {
  company: IPartnersCompany;
  /**
   * ★ NOT A LEGACY PROP. True when the current user does not own this company,
   * i.e. when every `PATCH /projects/companies/<id>` from these fields would
   * come back 404 (`PartnersPage.editableIds`). It puts `readonly`,
   * `tabindex="-1"` and the app-wide `non-clickable` class (`common.css` —
   * `pointer-events:none`) on all nine inputs. That trio is what
   * the legacy AHU forms use for a read-only field, and it is enough on its
   * own: an input that takes no pointer events and no tab stop never reaches
   * `:focus`, so its ✕ (`input:focus + .text-clear`, common.css) never appears.
   * The `<i>` elements are NOT removed — the `input + i + .placeholder-label`
   * sibling triple floats every label — and a click on one at `opacity:0` is
   * caught by `PartnersPage.clearTexField`'s ownership guard.
   */
  readOnly?: boolean;
}

/** Handlebars renders `{{field}}` as `""` for null — the value attribute is
 * always present and empty, never absent. */
const v = (value: string | null | undefined) => value ?? '';

const AddressForm = ({ company, readOnly = false }: AddressFormProps) => (
  <div className="col-xs-5 address-content" data-id={company.id}>
    {/* --- Address Line 1 (tmpl:107-117) --- */}
    <div className="row every-row">
      <div className="col-xs-12">
        <div className="">
          <div className="col-xs-1 col-icon-block">
            <span className="icon-location" />
          </div>
          <div className="col-xs-11 col-input-block">
            <input
              autoComplete="new-password"
              className={readOnly ? 'address_field non-clickable' : 'address_field'}
              type="text"
              name="address_line_one"
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : undefined}
              style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
              defaultValue={v(company.address_line_one)}
              placeholder="Address Line 1"
            />
            <i className="text-clear icon-close-1" />{' '}
            <span className="placeholder-label">Address Line 1</span>
          </div>
        </div>
      </div>
    </div>

    {/* --- Address Line 2 (tmpl:118-127) --- */}
    <div className="row every-row">
      <div className="col-xs-12">
        <div className="">
          <div className="col-xs-11 col-xs-offset-1 col-input-block">
            <input
              autoComplete="new-password"
              className={readOnly ? 'address_field non-clickable' : 'address_field'}
              type="text"
              name="address_line_two"
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : undefined}
              style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
              defaultValue={v(company.address_line_two)}
              placeholder="Address Line 2"
            />
            <i className="text-clear icon-close-1" />{' '}
            <span className="placeholder-label">Address Line 2</span>
          </div>
        </div>
      </div>
    </div>

    {/* --- City + State (tmpl:128-147) --- */}
    <div className="row every-row">
      <div className="col-xs-6 col-xs-offset-1">
        <div className="">
          <div className="col-xs-12 col-input-block">
            <input
              autoComplete="new-password"
              className={readOnly ? 'address_field non-clickable' : 'address_field'}
              type="text"
              name="city"
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : undefined}
              style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
              placeholder="City"
              defaultValue={v(company.city)}
              maxLength={30}
            />
            <i className="text-clear icon-close-1" />{' '}
            <span className="placeholder-label-city">City</span>
          </div>
        </div>
      </div>
      <div className="col-xs-5">
        <div className="">
          <div className="col-xs-12 col-input-block">
            <input
              autoComplete="new-password"
              className={readOnly ? 'address_field non-clickable' : 'address_field'}
              type="text"
              name="state"
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : undefined}
              style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
              placeholder="State"
              defaultValue={v(company.state)}
              maxLength={5}
            />
            <i className="text-clear icon-close-1" />{' '}
            <span className="placeholder-label-address-state">State</span>
          </div>
        </div>
      </div>
    </div>

    {/* --- Postal Code + Country (tmpl:148-167) --- */}
    <div className="row every-row">
      <div className="col-xs-4 col-xs-offset-1">
        <div className="">
          <div className="col-xs-12 col-input-block">
            <input
              autoComplete="new-password"
              className={readOnly ? 'address_field non-clickable' : 'address_field'}
              type="text"
              name="postal_code"
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : undefined}
              style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
              placeholder="Postal Code"
              defaultValue={v(company.postal_code)}
              maxLength={12}
            />
            <i className="text-clear icon-close-1" />{' '}
            <span className="placeholder-label-postal-code">Postal Code</span>
          </div>
        </div>
      </div>
      <div className="col-xs-7">
        <div className="">
          <div className="col-xs-12 col-input-block">
            <input
              autoComplete="new-password"
              className={readOnly ? 'address_field non-clickable' : 'address_field'}
              type="text"
              name="country"
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : undefined}
              style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
              placeholder="Country"
              defaultValue={v(company.country)}
              maxLength={15}
            />
            <i className="text-clear icon-close-1" />{' '}
            <span className="placeholder-label-address-country">Country</span>
          </div>
        </div>
      </div>
    </div>

    {/* --- Telephone (Work) (tmpl:168-181) --- */}
    <div className="row every-row">
      <div className="col-xs-12">
        <div className="">
          <div className="col-xs-1 col-icon-block">
            {/* `address_field` on a SPAN — a real class in the legacy DOM. */}
            <span className="address_field icon-phone" />
          </div>
          <div className="col-xs-11 col-input-block">
            <input
              autoComplete="new-password"
              className={`address_field phone-number-input${readOnly ? ' non-clickable' : ''}`}
              type="text"
              name="tel_office"
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : undefined}
              style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
              placeholder="Telephone (Work)"
              defaultValue={v(company.tel_office)}
              minLength={0}
              maxLength={20}
            />
            <i className="address-text-clear text-clear icon-close-1" />{' '}
            <span className="placeholder-label">Telephone (Work)</span>
          </div>
        </div>
      </div>
    </div>

    {/* --- Email (tmpl:182-195) --- */}
    <div className="row every-row">
      <div className="col-xs-12">
        <div className="">
          <div className="col-xs-1 col-icon-block">
            <span className="icon-email" />
          </div>
          <div className="col-xs-11 col-input-block">
            {/* type=email is what makes `checkValidity()` gate the 7c PATCH. */}
            <input
              autoComplete="new-password"
              className={`address_field press_english${readOnly ? ' non-clickable' : ''}`}
              type="email"
              name="email"
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : undefined}
              style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
              placeholder="Email"
              defaultValue={v(company.email)}
            />
            <i className="address-text-clear  text-clear icon-close-1" />{' '}
            <span className="placeholder-label">Email</span>
          </div>
        </div>
      </div>
    </div>

    {/* --- Notes (tmpl:196-209) --- */}
    <div className="row every-row">
      <div className="col-xs-12">
        <div className="">
          <div className="col-xs-1 col-icon-block">
            <span className="icon-chart" />
          </div>
          <div className="col-xs-11 col-input-block">
            <input
              autoComplete="new-password"
              className={readOnly ? 'address_field non-clickable' : 'address_field'}
              type="text"
              name="notes"
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : undefined}
              style={readOnly ? NON_CLICKABLE_OPAQUE : undefined}
              placeholder="Notes"
              defaultValue={v(company.notes)}
            />
            <i className="text-clear icon-close-1" />{' '}
            <span className="placeholder-label">Notes</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default AddressForm;
